import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "chart-heading" }
const _hoisted_2 = {
  key: 0,
  class: "segmented-control",
  role: "group",
  "aria-label": "Период динамики"
}
const _hoisted_3 = {
  key: 0,
  class: "ui-empty"
}
const _hoisted_4 = {
  key: 1,
  role: "alert",
  class: "ui-alert ui-alert--error"
}
const _hoisted_5 = {
  key: 2,
  class: "ui-empty"
}
const _hoisted_6 = {
  key: 3,
  class: "chart-container"
}
const _hoisted_7 = {
  key: 4,
  class: "chart-stats"
}

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

import { ref, computed, watch, onMounted } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
} from 'chart.js';

interface Props {
  title?: string;
  propertyId?: number | null;
  contactPersonId?: number | null;
  showPeriodFilter?: boolean;
  period?: 'week' | 'month' | 'quarter' | 'year' | 'all_time';
}

interface ChartDataPoint {
  date: string;
  average: number;
  count: number;
}

interface Stats {
  average: number;
  total: number;
  max: number;
  min: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SurveyChart',
  props: {
    title: { default: 'Динамика оценок' },
    propertyId: { default: null },
    contactPersonId: { default: null },
    showPeriodFilter: { type: Boolean, default: true },
    period: { default: 'month' }
  },
  emits: ['period-change'],
  setup(__props: any, { emit: __emit }) {

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const props = __props;

const emit = __emit;

const loading = ref(true);
const error = ref<string | null>(null);
const selectedPeriod = ref(props.period);
const chartDataPoints = ref<ChartDataPoint[]>([]);
const individualVotes = ref<Array<{ date: string; vote: number }>>([]);

watch(
  () => props.period,
  (newPeriod) => {
    selectedPeriod.value = newPeriod;
    loadData();
  }
);

const hasData = computed(() => chartDataPoints.value.length > 0);

const stats = computed<Stats>(() => {
  if (!hasData.value) {
    return { average: 0, total: 0, max: 0, min: 0 };
  }

  const averages = chartDataPoints.value.map((d) => d.average);
  const total = chartDataPoints.value.reduce((sum, d) => sum + d.count, 0);

  return {
    average: averages.reduce((sum, val) => sum + val, 0) / averages.length,
    total,
    max: Math.max(...averages),
    min: Math.min(...averages),
  };
});

const chartData = computed(() => {
  const labels = chartDataPoints.value.map((d) => d.date);
  const averageData = chartDataPoints.value.map((d) => d.average);

  // Группируем отдельные оценки по датам (периодам)
  const individualByDate: Record<string, number[]> = {};
  individualVotes.value.forEach((item) => {
    if (!individualByDate[item.date]) {
      individualByDate[item.date] = [];
    }
    individualByDate[item.date].push(item.vote);
  });

  // Находим максимальное количество одинаковых оценок в одной точке для масштабирования
  let maxVoteCount = 1;
  Object.values(individualByDate).forEach((votes) => {
    const counts: Record<number, number> = {};
    votes.forEach((v) => {
      counts[v] = (counts[v] || 0) + 1;
    });
    if (Object.keys(counts).length > 0) {
      const dayMax = Math.max(...Object.values(counts));
      if (dayMax > maxVoteCount) {
        maxVoteCount = dayMax;
      }
    }
  });

  const datasets: any[] = [
    {
      label: 'Средняя оценка',
      data: averageData,
      borderColor: '#2196f3',
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointRadius: 2,
      pointHoverRadius: 4,
    },
  ];

  // Добавляем точки отдельных оценок, если они есть
  // Группируем оценки по дате и значению, чтобы определить размер точки
  labels.forEach((label, index) => {
    if (individualByDate[label] && individualByDate[label].length > 0) {
      // Группируем оценки по значению для этой даты
      const votesByValue: Record<number, number> = {};
      individualByDate[label].forEach((vote) => {
        votesByValue[vote] = (votesByValue[vote] || 0) + 1;
      });

      // Создаем точку для каждого уникального значения оценки
      Object.keys(votesByValue).forEach((voteStr) => {
        const vote = parseInt(voteStr);
        const count = votesByValue[vote];

        // Радиус точки зависит от количества одинаковых оценок
        // Минимальный радиус 2 (при 1 отзыве), максимальный 12 (при maxVoteCount отзывах)
        const minRadius = 2;
        const maxRadius = 12;

        let pointRadius = minRadius;
        if (maxVoteCount > 1) {
          pointRadius = minRadius + (count - 1) * ((maxRadius - minRadius) / (maxVoteCount - 1));
        }

        const pointDataArray: (number | null)[] = labels.map((l, i) => {
          if (i === index) {
            return vote;
          }
          return null;
        });

        datasets.push({
          label: '',
          data: pointDataArray,
          pointRadius: pointRadius,
          pointHoverRadius: pointRadius + 2,
          pointBackgroundColor: 'rgba(255, 152, 0, 0.6)',
          pointBorderColor: '#ff9800',
          pointBorderWidth: 1,
          showLine: false,
          borderWidth: 0,
          voteCount: count,
          voteValue: vote,
        });
      });
    }
  });

  return {
    labels,
    datasets,
  };
});

const chartOptions = computed<ChartOptions<'line'>>(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.datasetIndex === 0) {
              // Средняя оценка
              const point = chartDataPoints.value[context.dataIndex];
              const yValue = context.parsed.y ?? 0;
              return [`Средняя оценка: ${yValue.toFixed(1)}`, `Количество отзывов: ${point.count}`];
            } else {
              // Отдельные оценки
              const dataset = context.dataset as any;
              const yValue = context.parsed.y ?? 0;
              const count = dataset.voteCount || 1;
              if (count > 1) {
                return [`Оценка: ${yValue}`, `Количество: ${count}`];
              }
              return `Оценка: ${yValue}`;
            }
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        min: 0,
        max: 11,
        ticks: {
          stepSize: 1,
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        type: 'category',
        grid: {
          display: false,
        },
      },
    },
  };
});

function onPeriodChange() {
  emit('period-change', selectedPeriod.value);
  loadData();
}

async function loadData() {
  loading.value = true;
  error.value = null;

  try {
    const params: any = {
      period: selectedPeriod.value,
    };

    if (props.propertyId) {
      params.property_id = props.propertyId;
    }

    if (props.contactPersonId) {
      params.contact_person_id = props.contactPersonId;
    }

    const axios = (await import('axios')).default;
    const response = await axios.get('/api/surveys/chart_data/', { params });
    chartDataPoints.value = response.data.data || [];
    individualVotes.value = response.data.individual || [];
  } catch (e: any) {
    console.error('Error loading chart data:', e);
    error.value = 'Ошибка загрузки данных';
  } finally {
    loading.value = false;
  }
}

watch(
  () => [props.propertyId, props.contactPersonId, props.period],
  () => {
    selectedPeriod.value = props.period;
    loadData();
  }
);

onMounted(() => {
  selectedPeriod.value = props.period;
  loadData();
});

const chartPeriods = [
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'quarter', label: 'Квартал' },
  { value: 'year', label: 'Год' },
  { value: 'all_time', label: 'Всё время' },
] as const;

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Card), { class: "survey-chart" }, {
    default: _withCtx(() => [
      _createVNode(_unref(CardHeader), null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_unref(CardTitle), null, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(__props.title), 1)
              ]),
              _: 1
            }),
            (__props.showPeriodFilter)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(chartPeriods, (choice) => {
                    return _createVNode(_unref(Button), {
                      key: choice.value,
                      size: "sm",
                      variant: selectedPeriod.value === choice.value ? 'secondary' : 'ghost',
                      "aria-pressed": selectedPeriod.value === choice.value,
                      onClick: ($event: any) => {
              selectedPeriod.value = choice.value;
              onPeriodChange();
            }
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(choice.label), 1)
                      ]),
                      _: 2
                    }, 1032, ["variant", "aria-pressed", "onClick"])
                  }), 64))
                ]))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }),
      _createVNode(_unref(CardContent), null, {
        default: _withCtx(() => [
          (loading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_unref(Spinner)),
                _cache[0] || (_cache[0] = _createElementVNode("span", null, "Загрузка динамики…", -1))
              ]))
            : (error.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(error.value), 1))
              : (!hasData.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_5, "Нет данных для отображения"))
                : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createVNode(_unref(Line), {
                      data: chartData.value,
                      options: chartOptions.value
                    }, null, 8, ["data", "options"])
                  ])),
          (hasData.value)
            ? (_openBlock(), _createElementBlock("dl", _hoisted_7, [
                _createElementVNode("div", null, [
                  _cache[1] || (_cache[1] = _createElementVNode("dt", null, "Средняя оценка", -1)),
                  _createElementVNode("dd", null, _toDisplayString(stats.value.average.toFixed(1)), 1)
                ]),
                _createElementVNode("div", null, [
                  _cache[2] || (_cache[2] = _createElementVNode("dt", null, "Всего отзывов", -1)),
                  _createElementVNode("dd", null, _toDisplayString(stats.value.total), 1)
                ]),
                _createElementVNode("div", null, [
                  _cache[3] || (_cache[3] = _createElementVNode("dt", null, "Макс. оценка", -1)),
                  _createElementVNode("dd", null, _toDisplayString(stats.value.max), 1)
                ]),
                _createElementVNode("div", null, [
                  _cache[4] || (_cache[4] = _createElementVNode("dt", null, "Мин. оценка", -1)),
                  _createElementVNode("dd", null, _toDisplayString(stats.value.min), 1)
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})